// orbitx-frontend/src/components/trading/UserOrders.js
import React, { useEffect, useState } from 'react';
import { XMarkIcon, ClockIcon, ArrowPathIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { formatNumber, formatDate, formatOrderStatus, formatTradeType } from '../../utils/formatters';
import useTradingStore from '../../store/useTradingStore';
import useMarketStore from '../../store/useMarketStore';
import useAuthStore from '../../store/useAuthStore';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import API_CONFIG from '../../config/api';

const UserOrders = () => {
  const { isAuthenticated } = useAuthStore();
  const [activeTab, setActiveTab] = useState('open');
  const [recentTrades, setRecentTrades] = useState([]);
  const [isLoadingTrades, setIsLoadingTrades] = useState(false);
  const [marketData, setMarketData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const {
    userOrders,
    fetchUserOrders,
    cancelOrder,
    isLoading,
    getOrdersByStatus,
    mapOrderToComponentFormat,
    fetchRecentTrades
  } = useTradingStore();

  const {
    limitOrders,
    fetchLimitOrders,
    cancelLimitOrder,
    isLoading: isMarketLoading
  } = useMarketStore();

  // Fetch market data for quote currency information
  useEffect(() => {
    const fetchMarketData = async () => {
      try {
        const response = await fetch(`${API_CONFIG.BASE_URL}/v1/homepage-external-price/`);
        const data = await response.json();
        setMarketData(data);
      } catch (error) {
        console.error('Error fetching market data:', error);
      }
    };

    fetchMarketData();
  }, []);

  // Get quote currency from market data
  const getQuoteCurrency = (pairCode) => {
    if (!marketData || !marketData.pairs_data || !marketData.pairs_data[pairCode]) return 'USDT';
    return marketData.pairs_data[pairCode].pair_data?.quote?.code || 'USDT';
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    
    const fetchData = async () => {
      try {
        // Fetch orders based on active tab
        let params = {};
        switch (activeTab) {
          case 'open':
            params = { state: 0 }; // Open orders
            break;
          case 'filled':
            params = { state: 1 }; // Filled orders
            break;
          case 'cancelled':
            params = { state: 2 }; // Cancelled orders
            break;
          case 'trade':
            // For trade history, fetch recent trades
            await fetchRecentTradesData();
            return;
          default:
            params = {}; // All orders
        }

        // Fetch from both stores
        await Promise.all([
          fetchUserOrders(params),
          fetchLimitOrders(params)
        ]);
      } catch (error) {
        console.error('Error fetching user orders:', error);
      }
    };

    fetchData();
  }, [fetchUserOrders, fetchLimitOrders, activeTab, isAuthenticated]);

  // Function to map API trade data to component format
  const mapTradeToComponentFormat = (apiTrade) => {
    // Handle timestamp conversion - API returns Unix timestamp in milliseconds
    let createdTimestamp, updatedTimestamp;
    try {
      if (typeof apiTrade.created === 'number') {
        createdTimestamp = new Date(apiTrade.created).toISOString();
      } else if (typeof apiTrade.created === 'string') {
        createdTimestamp = new Date(parseInt(apiTrade.created)).toISOString();
      } else {
        createdTimestamp = new Date().toISOString();
      }

      if (typeof apiTrade.updated === 'number') {
        updatedTimestamp = new Date(apiTrade.updated).toISOString();
      } else if (typeof apiTrade.updated === 'string') {
        updatedTimestamp = new Date(parseInt(apiTrade.updated)).toISOString();
      } else {
        updatedTimestamp = createdTimestamp;
      }
    } catch (error) {
      console.warn('Error parsing timestamp:', error);
      createdTimestamp = new Date().toISOString();
      updatedTimestamp = createdTimestamp;
    }

    const mappedTrade = {
      // Original API fields
      id: apiTrade.id,
      created: apiTrade.created,
      updated: apiTrade.updated,
      operation: apiTrade.operation,
      pair: apiTrade.pair,
      cancelled: apiTrade.cancelled,
      quantity: apiTrade.quantity,
      price: apiTrade.price,

      // Computed fields for display
      type: apiTrade.operation === 0 ? 'buy' : 'sell',
      amount: apiTrade.quantity,
      total: apiTrade.quantity * apiTrade.price,
      created_timestamp: createdTimestamp,
      updated_timestamp: updatedTimestamp,
      fee: (apiTrade.quantity * apiTrade.price * 0.001).toFixed(2), // Calculate fee as 0.1%
    };

    return mappedTrade;
  };

  // Function to fetch recent trades data
  const fetchRecentTradesData = async () => {
    setIsLoadingTrades(true);
    try {
      // Fetch from API
      const apiTrades = await fetchRecentTrades({ limit: 20 });

      if (apiTrades && apiTrades.results && apiTrades.results.length > 0) {
        // Map API trades to component format and show all results
        try {
          const mappedTrades = apiTrades.results.map(mapTradeToComponentFormat);
          setRecentTrades(mappedTrades); // Show all API trades
        } catch (mappingError) {
          console.error('Error mapping trades:', mappingError);
          setRecentTrades([]);
        }
      } else {
        setRecentTrades([]);
      }
    } catch (error) {
      console.error('Error fetching recent trades:', error);
      setRecentTrades([]);
    } finally {
      setIsLoadingTrades(false);
    }
  };

  const handleCancelOrder = async (orderId) => {
    try {
      // Find the order to get the original ID
      const order = processedOrders.find(o => o.id === orderId);
      if (!order) {
        toast.error('Order not found');
        return;
      }
      
      const originalOrderId = order.originalId || orderId;
      
      // Try to cancel from both stores
      try {
        await cancelOrder(originalOrderId);
        toast.success('Order cancelled successfully');
      } catch (error) {
        // If trading store fails, try market store
        await cancelLimitOrder(originalOrderId);
        toast.success('Order cancelled successfully');
      }

      // Refresh orders from both stores
      await Promise.all([
        fetchUserOrders(),
        fetchLimitOrders()
      ]);
    } catch (error) {
      toast.error(error.message || 'Failed to cancel order');
    }
  };

  // Process orders - combine orders from both stores
  const processedOrders = (() => {
    // Combine orders from both stores
    const allOrders = [...(userOrders || []), ...(limitOrders || [])];

    if (!allOrders || allOrders.length === 0) {
      return [];
    }

    // Both stores read the same backend endpoint. Deduplicate by the backend
    // order ID so an order is rendered once even when both stores contain it.
    const uniqueOrdersMap = new Map();
    
    allOrders.forEach((order) => {
      const uniqueKey = String(order.id);
      
      // If mapOrderToComponentFormat is available, use it, otherwise assume order is already in correct format
      let processedOrder;
      if (mapOrderToComponentFormat && typeof mapOrderToComponentFormat === 'function') {
        processedOrder = mapOrderToComponentFormat(order);
      } else if (order.state !== undefined) {
        // Fallback mapping if store function not available
        processedOrder = {
          id: order.id,
          originalId: order.id,
          pair: order.pair,
          type: order.operation === 0 ? 'buy' : 'sell',
          orderType: order.type === 0 ? 'limit' : order.type === 1 ? 'market' : 'stop',
          amount: order.quantity,
          price: order.price,
          filled: order.quantity - order.quantity_left,
          quantity_left: order.quantity_left,
          status: order.state === 0 ? (order.quantity_left === order.quantity ? 'open' : 'partial') :
            order.state === 1 ? 'filled' : 'cancelled',
          created_at: order.created,
          updated_at: order.updated,
        };
      } else {
        // Return as-is while retaining the backend ID for cancellation.
        processedOrder = {
          ...order,
          id: order.id,
          originalId: order.id
        };
      }
      
      uniqueOrdersMap.set(uniqueKey, processedOrder);
    });

    return Array.from(uniqueOrdersMap.values());
  })();

  // Filter orders by active tab
  const getFilteredOrders = () => {
    if (!processedOrders || processedOrders.length === 0) return [];

    switch (activeTab) {
      case 'open':
        return processedOrders.filter(order =>
          order.status === 'open' || order.status === 'partial'
        );
      case 'filled':
        return processedOrders.filter(order => order.status === 'filled');
      case 'cancelled':
        return processedOrders.filter(order => order.status === 'cancelled');
      default:
        return processedOrders;
    }
  };

  const orders = getFilteredOrders();

  // Reset page when tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  // Calculate pagination for trades
  const filteredTrades = recentTrades.filter(trade => trade && trade.id && trade.pair && trade.type !== undefined);
  const totalTradesPages = Math.ceil(filteredTrades.length / itemsPerPage);
  const startTradeIndex = (currentPage - 1) * itemsPerPage;
  const endTradeIndex = startTradeIndex + itemsPerPage;
  const paginatedTrades = filteredTrades.slice(startTradeIndex, endTradeIndex);

  // Calculate pagination for orders
  const totalOrdersPages = Math.ceil(orders.length / itemsPerPage);
  const startOrderIndex = (currentPage - 1) * itemsPerPage;
  const endOrderIndex = startOrderIndex + itemsPerPage;
  const paginatedOrders = orders.slice(startOrderIndex, endOrderIndex);

  // Get current pagination info based on active tab
  const currentTotalPages = activeTab === 'trade' ? totalTradesPages : totalOrdersPages;
  const currentTotalItems = activeTab === 'trade' ? filteredTrades.length : orders.length;

  const tabs = [
    {
      id: 'open',
      label: 'Open',
      shortLabel: 'Open',
      count: processedOrders.filter(order => order.status === 'open' || order.status === 'partial').length
    },
    {
      id: 'filled',
      label: 'History',
      shortLabel: 'History',
      count: processedOrders.filter(order => order.status === 'filled').length
    },
    {
      id: 'cancelled',
      label: 'Cancelled',
      shortLabel: 'Cancelled',
      count: processedOrders.filter(order => order.status === 'cancelled').length
    },
    {
      id: 'trade',
      label: 'Trades',
      shortLabel: 'Trades',
      count: recentTrades.length
    },
  ];

  // Show authentication prompt if user is not logged in
  if (!isAuthenticated) {
    return (
      <div className="bg-[#010615] rounded-xl border border-gray-700 p-6">
        <div className="text-center py-12">
          <ClockIcon className="h-16 w-16 mx-auto mb-4 text-white" />
          <h3 className="text-xl font-semibold text-white mb-2">Sign in to view your orders</h3>
          <p className="text-white mb-6">Track your trading activity and order history</p>
          <div className="flex justify-center space-x-4">
            <Link 
              to="/login" 
              className="bg-[#0E1696] hover:bg-[#0E1696] !text-white px-6 py-2 rounded-lg font-medium transition-colors"
            >
              Sign In
            </Link>
            <Link 
              to="/register" 
              className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
            >
              Register
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'open':
        return 'text-white bg-blue-400/10';
      case 'partial':
        return 'text-white bg-[#0E1696]/10';
      case 'filled':
        return 'text-white bg-green-400/10';
      case 'cancelled':
        return 'text-red-400 bg-red-400/10';
      default:
        return 'text-white bg-gray-400/10';
    }
  };

  const getTypeColor = (type) => {
    // Handle both string and numeric values
    if (type === 'buy' || type === 0) return 'text-white';
    if (type === 'sell' || type === 1) return 'text-red-400';
    return 'text-white'; // Default fallback
  };

  return (
    <div className="bg-gray-900 text-white rounded-lg border border-gray-700 h-full">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 px-3 sm:px-4 py-3 rounded-t-lg">
        <div className="flex justify-between items-center">
          <h3 className="text-base sm:text-lg font-semibold">Orders</h3>
          <div className="flex items-center space-x-2">
            {activeTab === 'trade' && (
              <button
                onClick={fetchRecentTradesData}
                disabled={isLoadingTrades}
                className="text-white hover:text-blue-500 transition-colors disabled:opacity-50 p-1"
                title="Refresh Trade History"
              >
                <ArrowPathIcon className={`h-4 w-4 sm:h-5 sm:w-5 ${isLoadingTrades ? 'animate-spin' : ''}`} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-gray-900 border-b border-gray-700">
        <div className="flex overflow-x-auto scrollbar-hide">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-shrink-0 px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium transition-all duration-200 relative group min-w-max ${
                activeTab === tab.id
                  ? 'text-white bg-blue-400/10 border-b-2 border-blue-400'
                  : 'text-white hover:text-white hover:bg-gray-800'
              }`}
            >
              <div className="flex items-center justify-center space-x-1 sm:space-x-2">
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.shortLabel}</span>
                {tab.count > 0 && (
                  <span className={`inline-flex items-center justify-center px-1.5 sm:px-2 py-0.5 text-xs font-bold leading-none rounded-full transition-all duration-200 ${
                    activeTab === tab.id
                      ? 'text-white bg-blue-500'
                      : 'text-white bg-gray-700 group-hover:bg-gray-600'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-y-auto">
          {activeTab === 'trade' ? (
            // Trade History Display
            <>
              {isLoadingTrades ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                </div>
              ) : filteredTrades.length === 0 ? (
                <div className="text-center py-8 px-4">
                  <div className="text-white text-sm">No trade history found</div>
                </div>
              ) : (
                <>
                <div className="divide-y divide-gray-700">
                  {paginatedTrades.map((trade) => {
                      try {
                        return (
                          <div key={trade.id} className="p-3 sm:p-4 hover:bg-gray-800 transition-colors">
                            {/* Mobile Layout */}
                            <div className="sm:hidden">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center space-x-2">
                                  <span className="font-medium text-sm">
                                    {trade.pair || 'Unknown'}
                                  </span>
                                  <span className={`text-xs font-medium px-2 py-1 rounded ${getTypeColor(trade.type)} bg-gray-800`}>
                                    {formatTradeType(trade.type || 'buy').toUpperCase()}
                                  </span>
                                </div>
                                <span className="text-xs text-white">
                                  {formatDate(trade.created_timestamp || new Date().toISOString(), 'time')}
                                </span>
                              </div>
                              
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div>
                                  <div className="text-white">Quantity</div>
                                  <div className="font-medium">
                                    {formatNumber(trade.quantity || 0, 2)}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-white">Price</div>
                                  <div className="font-medium">
                                    {formatNumber(trade.price || 0, 2)} {getQuoteCurrency(trade.pair)}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-white">Total</div>
                                  <div className="font-medium">
                                    {formatNumber(trade.total || 0, 2)} {getQuoteCurrency(trade.pair)}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-white">Status</div>
                                  <div className="font-medium">
                                    {trade.cancelled ? 'Cancelled' : 'Completed'}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Desktop Layout */}
                            <div className="hidden sm:block">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center space-x-3">
                                  <span className="font-medium">
                                    {trade.pair || 'Unknown'}
                                  </span>
                                  <span className={`text-sm font-medium ${getTypeColor(trade.type)}`}>
                                    {formatTradeType(trade.type || 'buy').toUpperCase()}
                                  </span>
                                  <span className="text-xs text-white">
                                    {formatDate(trade.created_timestamp || new Date().toISOString(), 'time')}
                                  </span>
                                </div>
                              </div>

                              <div className="grid grid-cols-3 gap-4 text-sm">
                                <div>
                                  <div className="text-white">Quantity</div>
                                  <div className="font-medium">
                                    {formatNumber(trade.quantity || 0, 2)}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-white">Price</div>
                                  <div className="font-medium">
                                    {formatNumber(trade.price || 0, 2)} {getQuoteCurrency(trade.pair)}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-white">Total</div>
                                  <div className="font-medium">
                                    {formatNumber(trade.total || 0, 2)} {getQuoteCurrency(trade.pair)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      } catch (error) {
                        console.error('Error rendering trade:', error, trade);
                        return null;
                      }
                    })}
                </div>
                {/* Pagination Controls for Trades */}
                {currentTotalPages > 1 && (
                  <div className="border-t border-gray-700 px-3 sm:px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-3">
                    <div className="flex items-center text-xs sm:text-sm text-white">
                      <span>
                        Showing {startTradeIndex + 1} to {Math.min(endTradeIndex, filteredTrades.length)} of {filteredTrades.length} trades
                      </span>
                    </div>
                    <div className="flex items-center space-x-1 sm:space-x-2">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="p-1.5 rounded-md border border-gray-600 text-white hover:text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title="Previous page"
                      >
                        <ChevronLeftIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                      </button>
                      <div className="flex items-center space-x-1">
                        {Array.from({ length: Math.min(5, currentTotalPages) }, (_, i) => {
                          let pageNum;
                          if (currentTotalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= currentTotalPages - 2) {
                            pageNum = currentTotalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              className={`px-2 sm:px-3 py-1 text-xs sm:text-sm rounded-md transition-colors ${
                                currentPage === pageNum
                                  ? 'bg-blue-500 text-white'
                                  : 'text-white hover:text-white hover:bg-gray-800 border border-gray-600'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(currentTotalPages, prev + 1))}
                        disabled={currentPage === currentTotalPages}
                        className="p-1.5 rounded-md border border-gray-600 text-white hover:text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title="Next page"
                      >
                        <ChevronRightIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                      </button>
                    </div>
                  </div>
                )}
                </>
              )}
            </>
          ) : (
            // Orders Display
            <>
              {(isLoading || isMarketLoading) ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                </div>
              ) : orders?.length === 0 ? (
                <div className="text-center py-8 px-4">
                  <div className="text-white text-sm">
                    No {activeTab} orders found
                  </div>
                </div>
              ) : (
                <>
                <div className="divide-y divide-gray-700">
                  {paginatedOrders?.map((order) => (
                    <div key={order.id} className="p-3 sm:p-4 hover:bg-gray-800 transition-colors">
                      {/* Mobile Layout */}
                      <div className="sm:hidden">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <span className="font-medium text-sm">
                              {order.pair}
                            </span>
                            <span className={`text-xs font-medium px-2 py-1 rounded ${getTypeColor(order.type)} bg-gray-800`}>
                              {formatTradeType(order.type).toUpperCase()}
                            </span>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(order.status)}`}>
                              {formatOrderStatus(order.status)}
                            </span>
                          </div>

                          {order.status === 'open' && (
                            <button
                              onClick={() => handleCancelOrder(order.id)}
                              className="text-white hover:text-red-500 transition-colors p-1"
                              title="Cancel Order"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <div className="text-white">Amount</div>
                            <div className="font-medium">
                              {formatNumber(order.amount, 2)}
                            </div>
                          </div>
                          <div>
                            <div className="text-white">Price</div>
                            <div className="font-medium">
                              {formatNumber(order.price, 2)} {getQuoteCurrency(order.pair)}
                            </div>
                          </div>
                          <div>
                            <div className="text-white">Filled</div>
                            <div className="font-medium">
                              {formatNumber(order.filled, 2)}
                            </div>
                          </div>
                          <div>
                            <div className="text-white">Progress</div>
                            <div className="font-medium">
                              {((order.filled / order.amount) * 100).toFixed(1)}%
                            </div>
                          </div>
                        </div>

                        {order.status === 'partial' && (
                          <div className="mt-2">
                            <div className="w-full bg-gray-700 rounded-full h-1">
                              <div
                                className="bg-[#0E1696] h-1 rounded-full transition-all duration-300"
                                style={{ width: `${(order.filled / order.amount) * 100}%` }}
                              ></div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Desktop Layout */}
                      <div className="hidden sm:block">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <span className="font-medium">
                              {order.pair}
                            </span>
                            <span className={`text-sm font-medium ${getTypeColor(order.type)}`}>
                              {formatTradeType(order.type).toUpperCase()}
                            </span>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(order.status)}`}>
                              {formatOrderStatus(order.status)}
                            </span>
                          </div>

                          {order.status === 'open' && (
                            <button
                              onClick={() => handleCancelOrder(order.id)}
                              className="text-white hover:text-red-500 transition-colors"
                              title="Cancel Order"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>

                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <div className="text-white">Amount</div>
                            <div className="font-medium">
                              {formatNumber(order.amount, 2)}
                            </div>
                          </div>
                          <div>
                            <div className="text-white">Price</div>
                            <div className="font-medium">
                              {formatNumber(order.price, 2)} {getQuoteCurrency(order.pair)}
                            </div>
                          </div>
                          <div>
                            <div className="text-white">Filled</div>
                            <div className="font-medium">
                              {formatNumber(order.filled, 2)} ({((order.filled / order.amount) * 100).toFixed(1)}%)
                            </div>
                          </div>
                        </div>

                        {order.status === 'partial' && (
                          <div className="mt-3">
                            <div className="flex justify-between text-xs text-white mb-1">
                              <span>Progress</span>
                              <span>{((order.filled / order.amount) * 100).toFixed(1)}%</span>
                            </div>
                            <div className="w-full bg-gray-700 rounded-full h-1">
                              <div
                                className="bg-[#0E1696] h-1 rounded-full transition-all duration-300"
                                style={{ width: `${(order.filled / order.amount) * 100}%` }}
                              ></div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {/* Pagination Controls for Orders */}
                {currentTotalPages > 1 && (
                  <div className="border-t border-gray-700 px-3 sm:px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-3">
                    <div className="flex items-center text-xs sm:text-sm text-white">
                      <span>
                        Showing {startOrderIndex + 1} to {Math.min(endOrderIndex, orders.length)} of {orders.length} orders
                      </span>
                    </div>
                    <div className="flex items-center space-x-1 sm:space-x-2">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="p-1.5 rounded-md border border-gray-600 text-white hover:text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title="Previous page"
                      >
                        <ChevronLeftIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                      </button>
                      <div className="flex items-center space-x-1">
                        {Array.from({ length: Math.min(5, currentTotalPages) }, (_, i) => {
                          let pageNum;
                          if (currentTotalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= currentTotalPages - 2) {
                            pageNum = currentTotalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              className={`px-2 sm:px-3 py-1 text-xs sm:text-sm rounded-md transition-colors ${
                                currentPage === pageNum
                                  ? 'bg-blue-500 text-white'
                                  : 'text-white hover:text-white hover:bg-gray-800 border border-gray-600'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(currentTotalPages, prev + 1))}
                        disabled={currentPage === currentTotalPages}
                        className="p-1.5 rounded-md border border-gray-600 text-white hover:text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title="Next page"
                      >
                        <ChevronRightIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                      </button>
                    </div>
                  </div>
                )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserOrders;
